import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useCoreIntegrity,
  useAuditChains,
  type CoreIntegrityPage,
} from "./use-integrity.ts";
import {
  isIntegrityEmpty,
  coreRowStatus,
  coreStateLabel,
  coreFailureReasonLabel,
  coreEventsHref,
  chainStatusLevel,
} from "./vocab.ts";
import type { AuditChain, CoreIntegrityRow } from "./use-integrity.ts";
import { CoreIntegrityTable } from "./CoreIntegrityTable.tsx";
import { AuditChainsTable } from "./AuditChainsTable.tsx";
import {
  DashboardGrid,
  KpiCard,
  StatusGrid,
  type StatusGridItem,
} from "../../components/observability/index.ts";
import "./integrity.css";

/**
 * The Integrity screen (task 11.9; design 12.2 Integrity row). It composes the three regions the
 * design calls for over two primary REST reads, all role `viewer`:
 *
 * - `GET /api/v1/integrity/core` → Protected_Core attestation state per instance (the PRIMARY
 *   snapshot the top-level state rests on).
 * - `GET /api/v1/integrity/audit-chains` → audit chains, each ALWAYS carrying verification level
 *   (`pending` when undetermined, grey-not-green), head position, export lag and first failure
 *   (`none` when clean). A chain row expands to its detail (`GET /integrity/audit-chains/{id}`),
 *   which also carries the chain's pinned-vs-observed KEY PINS.
 *
 * These are SNAPSHOTS with no time window (the integrity endpoints take no `from`/`to`), so there is
 * nothing to window; the shared time-range's live/pause flag still governs POLLING, so pausing
 * freezes the snapshot refresh exactly as it freezes the windowed screens (design 12.2, FR-13 AC2).
 * The core read drives the scaffold state; the audit-chains region renders its own loading/empty/
 * error state inside the ready screen, so a slow or empty chains read never blanks the whole page.
 *
 * It handles every state a screen must (task 11 preamble; design 12.3):
 * - restricted: the reads are viewer-level; an unknown/below-viewer role sees the restricted state.
 * - loading / error / empty / ready as the core query resolves; a cold-core waking error defers to
 *   the shell's banner (kept "loading" under it so there is no contradiction).
 */
export function IntegrityScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const { query: core } = useCoreIntegrity();
  const { query: chains } = useAuditChains();

  const state = resolveState(role, core, chains);

  return (
    <ScreenScaffold
      title="Integrity"
      subtitle="Protected_Core attestation state per instance, and audit chains that always show verification level, head position, export lag and first failure, with pinned vs observed keys per chain."
      state={state}
    >
      {core.data !== undefined && (
        <IntegrityBody core={core.data} chains={chains} live={live} />
      )}
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from the role and the two query statuses. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  core: ReturnType<typeof useCoreIntegrity>["query"],
  chains: ReturnType<typeof useAuditChains>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the integrity view is hidden until it is. The server enforces access regardless."
          : "The integrity view needs at least the viewer role.",
    };
  }

  if (core.data !== undefined) {
    const chainItems = chains.data?.items ?? [];
    if (isIntegrityEmpty(core.data.items, chainItems)) {
      return {
        kind: "empty",
        cause:
          "No Protected_Core state and no audit chains have been reported. Check that services are reporting their Protected_Core attestation and that sources are exporting audit batches.",
      };
    }
    return { kind: "ready" };
  }

  if (core.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (core.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(core.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useCoreIntegrity>["query"]["error"]): string {
  if (error === null) return "The integrity view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see integrity data. The server enforces access.";
    case "network":
      return "The integrity request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function IntegrityBody(props: {
  core: CoreIntegrityPage;
  chains: ReturnType<typeof useAuditChains>["query"];
  live: boolean;
}): ReactNode {
  const generatedAt = props.core.meta.generated_at;

  const chainItems = props.chains.data?.items;

  return (
    <div className="itg" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="itg__meta">
        {props.live ? "Live" : "Paused"} · snapshot generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
      </p>
      </div>

      {/* At-a-glance dashboard header built from the SAME core + audit-chain reads the detailed
          tables below render — a KpiCard summary row and a StatusGrid of the Protected_Core
          instances, each cell drilling to that instance's integrity events. The chain counts are
          shown only once the (secondary) chains read has resolved, so nothing is fabricated while it
          is still loading. The full tables stay below (observability-ui task 3.1). */}
      <DashboardGrid
        label="Integrity summary"
        className="itg-dashboard"
        kpis={integrityKpis(props.core.items, chainItems).map((kpi) => (
          <KpiCard key={kpi.key} {...kpi.props} />
        ))}
      >
        <StatusGrid
          title="Protected_Core instances"
          items={coreStatusItems(props.core.items)}
          emptyMessage="No Protected_Core state has been reported by any instance."
        />
      </DashboardGrid>

      <CoreIntegrityTable rows={props.core.items} />

      {/* The audit-chains region is secondary: it shows its own state so a slow/empty read never
          blanks the page. The Protected_Core table above already carried the screen to READY. */}
      <ChainsRegion chains={props.chains} />
    </div>
  );
}

/** A KpiCard plus a stable React key, so the body can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: Parameters<typeof KpiCard>[0];
}

/**
 * The headline integrity counts as KpiCards (observability-ui task 3.1). Every value is derived from
 * the SAME core + audit-chain reads the detailed tables render — nothing new is fetched and nothing
 * is fabricated. The Protected_Core counts are always shown (the core read carried the screen to
 * ready). The chain counts are shown ONLY once the secondary chains read has resolved; while it is
 * still loading those cards render their honest loading state rather than a fabricated zero. A
 * failing/unverifiable chain, or a non-verified core, is surfaced as attention-needed (a `down`
 * trend chip), never the healthy green.
 */
function integrityKpis(
  core: readonly CoreIntegrityRow[],
  chains: readonly AuditChain[] | undefined,
): readonly KeyedKpi[] {
  const verifiedCores = core.filter((r) => coreRowStatus(r) === "healthy").length;
  const unhealthyCores = core.length - verifiedCores;

  const chainsLoading = chains === undefined;
  const failingChains = (chains ?? []).filter((c) => {
    const level = chainStatusLevel(c.status);
    return level === "error" || level === "unknown";
  }).length;

  return [
    { key: "cores", props: { label: "Core instances", value: core.length } },
    {
      key: "verified-cores",
      props: {
        label: "Verified cores",
        value: verifiedCores,
        ...(unhealthyCores > 0 ? { delta: `${unhealthyCores} not`, trend: "down" as const } : {}),
      },
    },
    {
      key: "chains",
      props: chainsLoading
        ? { label: "Audit chains", value: "…", loading: true }
        : { label: "Audit chains", value: (chains ?? []).length },
    },
    {
      key: "failing-chains",
      props: chainsLoading
        ? { label: "Needs attention", value: "…", loading: true }
        : {
            label: "Needs attention",
            value: failingChains,
            ...(failingChains > 0 ? { delta: `${failingChains}`, trend: "down" as const } : {}),
          },
    },
  ];
}

/**
 * Map the Protected_Core rows onto the kit StatusGrid's cells (observability-ui task 3.1), reusing
 * the screen's existing vocabulary ({@link coreRowStatus} / {@link coreStateLabel}) so a cell reads
 * exactly as the detailed table's badge does — only an explicitly verified (and non-stale) core is
 * healthy; a stale reading downgrades to grey, never green (design 12.3). Each cell drills to that
 * instance's integrity events via the same {@link coreEventsHref} the detailed table uses. Rows that
 * need attention (non-healthy) sort first.
 */
function coreStatusItems(core: readonly CoreIntegrityRow[]): readonly StatusGridItem[] {
  const sorted = [...core].sort((a, b) => coreRank(a) - coreRank(b));
  return sorted.map((row): StatusGridItem => {
    const label = row.stale ? `${coreStateLabel(row.state)} (stale)` : coreStateLabel(row.state);
    return {
      id: `${row.source_id}:${row.instance_id}`,
      name: row.instance_id,
      status: coreRowStatus(row),
      statusLabel: label,
      metrics: [
        { label: "Service", value: row.service },
        ...(row.failure_reason !== null
          ? [{ label: "Reason", value: coreFailureReasonLabel(row.failure_reason) }]
          : []),
      ],
      href: coreEventsHref(row),
    };
  });
}

/** Sort rank: cells that need attention (not healthy) first, so problems surface at the top. */
function coreRank(row: CoreIntegrityRow): number {
  return coreRowStatus(row) === "healthy" ? 1 : 0;
}

/**
 * A secondary region wrapper for the audit chains: renders the table when the data is present, an
 * honest loading status while the first fetch is in flight, and a non-blocking error note when the
 * (non-waking) read failed — so one slow or failing chains read never takes down the whole screen.
 */
function ChainsRegion(props: {
  chains: ReturnType<typeof useAuditChains>["query"];
}): ReactNode {
  const { chains } = props;

  if (chains.data !== undefined) {
    return <AuditChainsTable chains={chains.data.items} />;
  }
  if (chains.isError && !chains.error.isWaking) {
    return (
      <div className="itg-secondary-state" data-kind="error" role="alert" aria-label="Audit chains">
        <strong>Audit chains unavailable</strong>
        <span>The audit chains could not load. The Protected_Core state above still applies.</span>
      </div>
    );
  }
  return (
    <div className="itg-secondary-state" role="status" aria-live="polite" aria-label="Audit chains">
      <strong>Loading audit chains…</strong>
    </div>
  );
}
